import { IdType } from 'epic/common-form/ReloadFormRequest';
import { quoteTypeToTracking } from 'models';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { RequestType } from 'models/productsReferential';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { batchFormQuoteReloadRequested } from 'state/batchForm/batchForm.action';
import { getBatchTabIdToReloadForm } from 'state/selectors';
import {
  getQuoteTypeByAvailableActions,
  getStaticFormQuoteId,
  getStaticPricerRfqId,
  getStaticProductSubType,
  getStaticProductType,
  getStaticWrapper,
} from 'state/selectors/common';
import type { Thunk } from 'state/state.common';
import { staticQuoteRequestedThunk } from 'state/static/staticForm.thunks';
import { batchWorkspaceProductSelectedThunk } from 'state/workspaces/batch/tabContent/tabContent.thunks';
import { getNewGuid } from 'utils/guid';
import { getToastUtils } from 'utils/toast';

import {
  workspaceTabActivated,
  workspaceTabCreated,
  workspaceTabPersistRequested,
  workspaceTabProductSelectionSubmitted,
} from '../workspace.actions';
import { staticWorkspaceTabSetQuote, staticWorkspaceTabUnsetQuote } from './static.actions';
import { getWorkspaceStaticTabQuoteId } from './static.selectors';

export function staticTabSetAssociatedQuoteThunk(tabId: string, quoteId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const oldQuoteId = getWorkspaceStaticTabQuoteId(state, tabId);
    if (quoteId !== oldQuoteId) {
      dispatch(staticWorkspaceTabSetQuote(tabId, quoteId));
      dispatch(workspaceTabPersistRequested(tabId, 'static'));
    }
  };
}

export function staticTabResetAssociatedQuoteThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const oldQuoteId = getWorkspaceStaticTabQuoteId(state, tabId);
    if (oldQuoteId !== undefined) {
      dispatch(staticWorkspaceTabUnsetQuote(tabId));
      dispatch(workspaceTabPersistRequested(tabId, 'static'));
    }
  };
}

export function staticWorkspaceTabProductSelectionSubmittedThunk(
  tabId: string,
  productType: string,
  productSubType: string,
  wrapper: string,
  requestType: RequestType | undefined,
  client: Nullable<ISalesCounterpart>,
): Thunk<void> {
  return dispatch => {
    dispatch(
      workspaceTabProductSelectionSubmitted(
        tabId,
        {
          productType,
          productSubType,
          wrapper,
          requestType,
        },
        client,
        'static',
      ),
    );
  };
}

export function staticWorkspaceRepriceQuoteThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getStaticFormQuoteId } }) => {
    const state = getState();
    const quoteId = getStaticFormQuoteId(state, tabId)!;
    const quoteType = getQuoteTypeByAvailableActions(state, 'static', tabId);
    if (quoteType) {
      logAnalytics.trackEvent('Primary market static', quoteTypeToTracking[quoteType]);
    }
    dispatch(staticQuoteRequestedThunk(tabId, quoteType!, quoteId));
  };
}

export function staticWorkspaceOpenInBatchWorkspaceThunk(tabId: string, tabName: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    const quoteId = getStaticFormQuoteId(state, tabId);
    const productType = getStaticProductType(state, tabId);
    const productSubType = getStaticProductSubType(state, tabId);
    const wrapper = getStaticWrapper(state, tabId);
    const pricerRfqId = getStaticPricerRfqId(state, tabId);

    if (!quoteId || !productType || !productSubType || !wrapper || !pricerRfqId) {
      return;
    }

    const matchedTabId = getBatchTabIdToReloadForm(state, productType, productSubType, wrapper);
    const newTabId = matchedTabId ?? getNewGuid();

    if (!matchedTabId) {
      dispatch(workspaceTabCreated(newTabId, 'newBatch', 'reprice'));
      dispatch(
        batchWorkspaceProductSelectedThunk(newTabId, productType, productSubType, wrapper, undefined, tabName, true),
      );
    }

    dispatch(workspaceTabActivated(newTabId, 'newBatch', 'reprice'));

    dispatch(
      batchFormQuoteReloadRequested(
        newTabId,
        getNewGuid(),
        quoteId,
        IdType.Guess,
        productType,
        productSubType,
        wrapper,
        quoteId,
        pricerRfqId,
        'ExoOne',
      ),
    );

    const toast = getToastUtils(dispatch);
    toast.success(
      {
        id: 'openInBatchWorkspace.toast.content',
        values: { nbOfQuotes: 1 },
      },
      { id: 'openInBatchWorkspace' },
    );
  };
}
